// ADW ticket consolidation + formatting (pure, no React deps — node-testable).
//
// Consolidates a flat list of positional tickets (e.g. "1-2-3", "1-2-4" @ same $)
// into the fewest allowable tickets. Two passes per bet-amount bucket:
//   1) BOX pass — extract maximal "all-legs-equal" boxes (largest runner-set first),
//      e.g. an Exacta reverse pair 1-7 + 7-1 -> box (1;7)-(1;7), or a full trifecta
//      box. This is what lets reverse pairs combine (they differ in *both* legs, so
//      the wheel pass alone can't merge them).
//   2) WHEEL pass — greedily merge the remaining singletons that are identical in
//      every leg but one, unioning that leg (1-2-3 + 1-2-4 -> 1-2-(3;4)).
// Both passes are lossless: expanding a box/wheel (honoring the "a runner can't
// occupy two legs of one combo" rule) reproduces exactly the original combos.

function _kCombos(arr, k) {
  const res = [];
  const rec = (start, chosen) => {
    if (chosen.length === k) { res.push(chosen.slice()); return; }
    for (let i = start; i < arr.length; i += 1) { chosen.push(arr[i]); rec(i + 1, chosen); chosen.pop(); }
  };
  rec(0, []);
  return res;
}

function _permsK(arr, k) {
  const res = [];
  const rec = (chosen, rest) => {
    if (chosen.length === k) { res.push(chosen.slice()); return; }
    for (let i = 0; i < rest.length; i += 1) {
      chosen.push(rest[i]);
      rec(chosen, rest.slice(0, i).concat(rest.slice(i + 1)));
      chosen.pop();
    }
  };
  rec([], arr);
  return res;
}

// Pull out maximal all-legs-equal boxes (largest runner-set first). Returns the box
// runner-sets consumed and the leftover singleton combos (arrays of ints).
function _extractBoxes(comboArrs, positions) {
  const present = new Set(comboArrs.map((a) => a.join('-')));
  const runners = [...new Set(comboArrs.flat())].sort((a, b) => a - b);
  const consumed = new Set();
  const boxes = [];
  if (runners.length <= 10) {
    for (let size = runners.length; size >= positions; size -= 1) {
      for (const B of _kCombos(runners, size)) {
        const ps = _permsK(B, positions).map((p) => p.join('-'));
        if (ps.length >= 2 && ps.every((k) => present.has(k) && !consumed.has(k))) {
          ps.forEach((k) => consumed.add(k));
          boxes.push(B.slice());
        }
      }
    }
  }
  const remaining = comboArrs.filter((a) => !consumed.has(a.join('-')));
  return { boxes, remaining };
}

export function consolidateTickets(tickets, wheel = true) {
  const byAmt = new Map();
  for (const t of tickets) {
    const amt = Number(t.amount);
    const arr = String(t.combo).split('-').map((s) => s.trim()).filter((s) => s !== '').map(Number);
    if (!byAmt.has(amt)) byAmt.set(amt, []);
    byAmt.get(amt).push(arr);
  }
  const out = [];
  for (const [amt, comboArrs] of byAmt) {
    const nLegs = comboArrs.length ? comboArrs[0].length : 0;

    // Pass 1: boxes.
    const { boxes, remaining } = _extractBoxes(comboArrs, nLegs);
    for (const B of boxes) {
      const sorted = B.slice().sort((a, c) => a - c);
      out.push({ legs: Array.from({ length: nLegs }, () => sorted.slice()), amount: amt });
    }

    // Pass 2: wheel-merge the leftovers (skipped when wheel=false — leftovers stay
    // as raw single combos; boxing in Pass 1 always runs so cost stays correct).
    let work = remaining.map((arr) => arr.map((v) => new Set([v])));
    let changed = wheel;
    while (changed) {
      changed = false;
      for (let i = 0; i < nLegs; i += 1) {
        const groups = new Map();
        for (const b of work) {
          const key = b.map((s, idx) => (idx === i ? '*' : [...s].sort().join(','))).join('|');
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(b);
        }
        const nb = [];
        for (const members of groups.values()) {
          if (members.length > 1) {
            const merged = members[0].map((s) => new Set(s));
            for (let m = 1; m < members.length; m += 1) {
              for (const v of members[m][i]) merged[i].add(v);
            }
            nb.push(merged);
            changed = true;
          } else {
            nb.push(members[0]);
          }
        }
        work = nb;
      }
    }
    for (const b of work) {
      out.push({ legs: b.map((s) => [...s].map(Number).sort((a, c) => a - c)), amount: amt });
    }
  }
  return out;
}

// Ticket minimum: after consolidation, a ticket whose cost (stake x combos) is below
// the ticket minimum is raised to the next base step that reaches it. Ticket min
// defaults to the base, except every Exacta ticket is $2. Mutates in place; returns
// how many tickets were raised.
// Wheel-collapse gate: always consolidate when the ticket minimum exceeds the base
// (so fewer singles need raising); otherwise only above the admin threshold.
export const shouldWheel = (count, threshold, base, ticketMin) =>
  (!!base && !!ticketMin && ticketMin > base + 1e-9) || (count || 0) > (threshold ?? 0);

export const defaultTicketMin = (pool, base) => (pool === 'EX' ? Math.max(2, base || 0) : base);

export function applyExactaSingleMin(consTickets, pool, base, ticketMin) {
  let bumped = 0;
  const step = base || (pool === 'EX' ? 1 : 0);
  const min = ticketMin ?? (step ? defaultTicketMin(pool, step) : 0);
  if (!step || !min || min <= step + 1e-9) return 0;
  for (const t of consTickets) {
    const n = expandCount(t.legs);
    if (n && t.amount * n < min - 1e-9) {
      t.amount = Math.round(Math.ceil(min / n / step - 1e-9) * step * 100) / 100;
      bumped += 1;
    }
  }
  return bumped;
}

export const singleMinNote = (pool, n, ticketMin) =>
  `${n} ${pool === 'EX' ? 'exacta' : pool === 'TRI' ? 'trifecta' : 'superfecta'} ticket${n > 1 ? 's' : ''} → $${Number(ticketMin ?? (pool === 'EX' ? 2 : 1)).toFixed(2)} ticket min`;

// XpressBet combo: legs joined by "-", multi-runner legs as "(a;b;c)".
export function comboToXpress(legs) {
  return legs.map((l) => (l.length === 1 ? String(l[0]) : `(${l.join(';')})`)).join('-');
}

// AmWager combo: legs joined by "/", multi-runner legs as "a,b,c".
export function comboToAmwager(legs) {
  return legs.map((l) => l.join(',')).join('/');
}

// AmWager combo FIELD for the CSV line — wrapped in double quotes when it contains
// commas (multi-runner legs) so the embedded commas don't break the columns:
// e.g. "2,1/2,1". Single combos (3/1/4) stay unquoted.
export function amwagerComboField(legs) {
  const c = comboToAmwager(legs);
  return c.includes(',') ? `"${c}"` : c;
}

// Count how many combos a consolidated box expands to (honoring no-repeat-runner).
function expandCount(legs) {
  let count = 0;
  const rec = (i, used) => {
    if (i === legs.length) { count += 1; return; }
    for (const r of legs[i]) {
      if (used.has(r)) continue;
      used.add(r); rec(i + 1, used); used.delete(r);
    }
  };
  rec(0, new Set());
  return count;
}

// The ACTUAL amount that will be wagered at the window for a pool once tickets are
// consolidated and the single-exacta $2 minimum is applied — which can exceed the
// on-screen total. A consolidated wheel stores the PER-COMBO stake, so its cost is
// amount × (# combos it covers). Returns { total, bumped }.
export function actualPlayTotal(tickets, pool, base, ticketMin, wheel = true) {
  const cons = consolidateTickets(tickets || [], wheel);
  const bumped = applyExactaSingleMin(cons, pool, base, ticketMin);
  const total = Math.round(cons.reduce((s, t) => s + t.amount * expandCount(t.legs), 0) * 100) / 100;
  return { total, bumped };
}
